/** @format — Listings with documents query */

import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/services/python-api/queryKeys";
import { listListingsWithDocuments } from "@/services/python-api/documentsService";
import type { ListingWithDocuments } from "../types";

export const LISTINGS_PAGE_SIZE = 20;

function getNextListingsPage(
  lastPage: { data?: { listings?: ListingWithDocuments[]; total?: number } },
  allPages: Array<{ data?: { listings?: ListingWithDocuments[]; total?: number } }>,
) {
  const batch = lastPage?.data?.listings?.length ?? 0;
  const loaded = allPages.reduce((sum, page) => sum + (page?.data?.listings?.length ?? 0), 0);
  const total = lastPage?.data?.total;

  if (batch < LISTINGS_PAGE_SIZE) return undefined;
  if (typeof total === "number" && loaded >= total) return undefined;
  return allPages.length;
}

export function useDocumentsListings({ search }: { search: string }) {
  const query = useInfiniteQuery({
    queryKey: [QUERY_KEYS.DOCUMENTS, "listings", search.trim()],
    queryFn: async ({ pageParam = 0 }) => {
      try {
        return await listListingsWithDocuments({
          search: search.trim() || undefined,
          page: pageParam,
          limit: LISTINGS_PAGE_SIZE,
        });
      } catch {
        return { data: { listings: [], total: 0 } };
      }
    },
    initialPageParam: 0,
    getNextPageParam: getNextListingsPage,
    staleTime: 30_000,
    retry: false,
  });

  const listings = useMemo(() => {
    return (
      query.data?.pages?.flatMap((page) => page?.data?.listings ?? []) ?? []
    ) as ListingWithDocuments[];
  }, [query.data]);

  const total = query.data?.pages?.[0]?.data?.total ?? listings.length;

  return {
    listings,
    total,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    loadMore: query.fetchNextPage,
  };
}
